import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [actAs, setActAsState] = useState(() => JSON.parse(localStorage.getItem("actAsCompany") || "null"));

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      if (localStorage.getItem("activeCompany") && e?.response?.status === 403) {
        localStorage.removeItem("activeCompany");
        try { const { data } = await api.get("/auth/me"); setUser(data); return; } catch { /* fall through */ }
      }
      setUser(false);
    }
  }, []);

  const setActiveCompany = async (id) => {
    if (id) localStorage.setItem("activeCompany", id);
    else localStorage.removeItem("activeCompany");
    await refreshMe();
  };

  useEffect(() => {
    refreshMe();
    const onLogout = () => setUser(false);
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, [refreshMe]);

  const setActAs = (c) => {
    if (c) localStorage.setItem("actAsCompany", JSON.stringify(c));
    else localStorage.removeItem("actAsCompany");
    setActAsState(c);
  };

  const login = async (email, password) => {
    setActAs(null);
    localStorage.removeItem("activeCompany");
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setActAs(null);
    localStorage.removeItem("activeCompany");
    setUser(false);
  };

  const isSuper = user && user.platform_role === "super_admin";
  const role = !user ? null : isSuper ? (actAs ? "admin" : "super_admin") : user.tenant_role;
  const companyName = isSuper ? actAs?.name : user?.company?.name;
  const companyKey = isSuper ? actAs?.id || "platform" : user?.company_id;

  return (
    <AuthCtx.Provider value={{ user, role, isSuper, actAs, setActAs, login, logout, companyName, companyKey, refreshMe, setActiveCompany }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
